import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useStore } from '../data/store';
import { Users, Calendar, UserPlus, Heart, ShoppingBag, Trophy } from 'lucide-react';
import { formatTime } from '../lib/utils';

function Home() {
  const [contentVisible, setContentVisible] = useState(false);
  const { games } = useStore();

  useEffect(() => {
    setContentVisible(true);
  }, []);

  // Sort games by date
  const sortedGames = [...games].sort((a, b) => {
    const dateA = new Date(a.date).getTime();
    const dateB = new Date(b.date).getTime();
    return dateA - dateB;
  });

  // Split games into past and upcoming
  const now = new Date().getTime();
  const pastGames = sortedGames.filter(game => new Date(game.date).getTime() < now);
  const upcomingGames = sortedGames.filter(game => new Date(game.date).getTime() >= now);

  // Get the most recent games and next upcoming games
  const recentGames = pastGames.slice(-3).reverse();
  const nextGames = upcomingGames.slice(0, 3);

  return (
    <div className="min-h-screen flex flex-col bg-[#111111]">
      <Navbar />
      
      {/* Hero Section */}
      <div className="relative min-h-[85vh] flex items-center justify-center mt-24">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 overflow-hidden">
          <img 
            src="https://images.unsplash.com/photo-1562041392-404278da3c43?auto=format&fit=crop&q=80&w=2000&h=1200"
            alt="Baseball Diamond at Sunset"
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#111111] via-[#111111]/90 to-[#111111]" />
          <div className="absolute inset-0 bg-[#111111]/50" />
          
          {/* Cardinals Logo Background Pattern */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0 grid grid-cols-3 gap-8 p-8">
              {[...Array(9)].map((_, i) => (
                <div key={i} className="relative">
                  <img 
                    src="https://i.postimg.cc/fJxNP8LM/lc-logo.png"
                    alt=""
                    className="w-full h-full object-contain opacity-20"
                    style={{
                      filter: 'grayscale(100%) contrast(0.5) brightness(1.2)'
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Hero Content */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className={`max-w-3xl text-center ${contentVisible ? 'animate-slide-up' : 'opacity-0'}`}>
            <div className="mb-8 inline-flex items-center justify-center relative">
              <div className="absolute inset-0 bg-cardinal opacity-20 blur-3xl rounded-full transform scale-150" />
              <img 
                src="https://i.postimg.cc/fJxNP8LM/lc-logo.png"
                alt="Cardinals Logo"
                className="w-40 h-40 object-contain animate-float relative"
                loading="eager"
              />
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 sm:mb-8 leading-tight px-4">
              Lincoln County Cardinals
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-gray-300 mb-8 sm:mb-12 leading-relaxed px-4">
              Lincoln County's Premier Baseball Experience
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap gap-4 sm:gap-6 justify-center px-4">
              <Link
                to="/players"
                className="inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 bg-cardinal hover:bg-cardinal-hover text-white rounded-lg text-base sm:text-lg font-semibold transition-all hover:scale-105 hover:shadow-glow"
              >
                Meet the Team
              </Link>
              <Link
                to="/schedule"
                className="inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 bg-white/10 hover:bg-white/20 text-white rounded-lg text-base sm:text-lg font-semibold transition-all hover:scale-105 backdrop-blur-sm"
              >
                Game Schedule
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Banner Section */}
      <div className="w-full bg-[#111111] pt-4 pb-12 sm:pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <img
            src="https://i.postimg.cc/ZKVkGfmN/download.jpg"
            alt="Cardinals Banner"
            className="w-full h-auto rounded-xl shadow-2xl"
          />
        </div>
      </div>

      {/* Game Results and Schedule Section */}
      <div className={`bg-[#1A1A1A] py-16 sm:py-20 md:py-28 ${contentVisible ? 'animate-fade-in [animation-delay:300ms]' : 'opacity-0'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-8 sm:mb-12 text-center">Recent Games & Schedule</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 md:gap-12">
            <div className="bg-[#222222] p-6 sm:p-8 rounded-xl shadow-lg border border-white/5">
              <h3 className="text-xl sm:text-2xl font-semibold text-white mb-6 flex items-center gap-3">
                <Trophy className="h-5 w-5 sm:h-6 sm:w-6 text-cardinal" />
                Latest Results
              </h3>
              {recentGames.length > 0 ? (
                <div className="space-y-5">
                  {recentGames.map(game => (
                    <div key={game.id} className="border-b border-gray-700 last:border-0 pb-5 last:pb-0">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-gray-400">{game.date}</p>
                          <p className="text-white font-semibold">
                            {game.location === 'Home' ? `vs ${game.opponent}` : `at ${game.opponent}`}
                          </p>
                          {game.isDoubleHeader && (
                            <span className="inline-block mt-1 px-2 py-0.5 bg-cardinal text-white text-xs font-semibold rounded">
                              Double Header
                            </span>
                          )}
                        </div>
                        {game.score && (
                          <div className="text-right">
                            {game.isDoubleHeader ? (
                              <div className="space-y-1">
                                {game.score.game1 && (
                                  <div>
                                    <p className="text-xs text-gray-400">Game 1</p>
                                    <p className={`font-bold ${
                                      (game.score.game1.cardinals ?? 0) > (game.score.game1.opponent ?? 0)
                                        ? 'text-green-500'
                                        : (game.score.game1.cardinals ?? 0) < (game.score.game1.opponent ?? 0)
                                        ? 'text-red-500'
                                        : 'text-gray-300'
                                    }`}>
                                      {game.score.game1.cardinals ?? '-'} - {game.score.game1.opponent ?? '-'}
                                    </p>
                                  </div>
                                )}
                                {game.score.game2 && (
                                  <div>
                                    <p className="text-xs text-gray-400">Game 2</p>
                                    <p className={`font-bold ${
                                      (game.score.game2.cardinals ?? 0) > (game.score.game2.opponent ?? 0)
                                        ? 'text-green-500'
                                        : (game.score.game2.cardinals ?? 0) < (game.score.game2.opponent ?? 0)
                                        ? 'text-red-500'
                                        : 'text-gray-300'
                                    }`}>
                                      {game.score.game2.cardinals ?? '-'} - {game.score.game2.opponent ?? '-'}
                                    </p>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <>
                                <p className="text-sm text-gray-400">Final Score</p>
                                <p className={`font-bold ${
                                  (game.score.cardinals ?? 0) > (game.score.opponent ?? 0)
                                    ? 'text-green-500'
                                    : (game.score.cardinals ?? 0) < (game.score.opponent ?? 0)
                                    ? 'text-red-500'
                                    : 'text-gray-300'
                                }`}>
                                  {game.score.cardinals ?? '-'} - {game.score.opponent ?? '-'}
                                </p>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-center py-4">No recent games</p>
              )}
            </div>

            <div className="bg-[#222222] p-6 sm:p-8 rounded-xl shadow-lg border border-white/5">
              <h3 className="text-xl sm:text-2xl font-semibold text-white mb-6 flex items-center gap-3">
                <Calendar className="h-5 w-5 sm:h-6 sm:w-6 text-cardinal" />
                Upcoming Games
              </h3>
              {nextGames.length > 0 ? (
                <div className="space-y-5">
                  {nextGames.map(game => (
                    <div key={game.id} className="border-b border-gray-700 last:border-0 pb-5 last:pb-0">
                      <p className="text-gray-400">{game.date}</p>
                      <p className="text-white font-semibold">
                        {game.location === 'Home' ? `vs ${game.opponent}` : `at ${game.opponent}`}
                      </p>
                      <div className="flex items-center gap-4 mt-1 text-sm text-gray-400">
                        <span>{formatTime(game.time)}{game.isDoubleHeader && game.time2 ? ` & ${formatTime(game.time2)}` : ''}</span>
                        <span>•</span>
                        <span>{game.venue}</span>
                      </div>
                      {game.isDoubleHeader && (
                        <span className="inline-block mt-2 px-2 py-0.5 bg-cardinal text-white text-xs font-semibold rounded">
                          Double Header
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-center py-4">No upcoming games scheduled</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Access Cards */}
      <div className={`bg-[#111111] py-16 sm:py-20 md:py-28 ${contentVisible ? 'animate-fade-in' : 'opacity-0'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 sm:gap-8">
            {[
              { icon: Users, title: 'Meet the Team', desc: 'Get to know our talented roster', href: '/players' },
              { icon: Calendar, title: 'Game Schedule', desc: 'View our upcoming matchups', href: '/schedule' },
              { icon: UserPlus, title: 'Join the Team', desc: 'Be part of our baseball family', href: '/join' },
              { icon: Heart, title: 'Support Us', desc: 'Help us hit it out of the park', href: '/donate' },
              { icon: ShoppingBag, title: 'Team Shop', desc: 'Gear up with Cardinals merch', href: '/shop' }
            ].map((item, index) => (
              <Link 
                key={item.title} 
                to={item.href}
                className={`group transition-all hover:-translate-y-2 ${contentVisible ? `animate-scale-in [animation-delay:${index * 100}ms]` : 'opacity-0'}`}
              >
                <div className="bg-[#222222] p-6 sm:p-8 rounded-xl shadow-lg hover:shadow-2xl hover:bg-[#2A2A2A] transition-all h-full border border-white/5">
                  <item.icon className="h-8 w-8 sm:h-10 sm:w-10 text-cardinal mb-4 sm:mb-6 group-hover:scale-110 transition-transform" />
                  <h3 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3 text-white">{item.title}</h3>
                  <p className="text-sm sm:text-base text-gray-400">{item.desc}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default Home;